<template>
  <div>
    <div class="d-flex flex-wrap align-items-center gap-3 mb-3 range-picker-row">
      <div class="d-flex align-items-center gap-2">
        <label class="form-label mb-0 me-1">{{ $t('tools.dateRange') }}</label>
        <el-date-picker
          v-model="dateRange"
          type="daterange"
          :unlink-panels="!isMobileViewport"
          range-separator="—"
          :start-placeholder="$t('common.startDate') || 'Start'"
          :end-placeholder="$t('common.endDate') || 'End'"
          format="DD/MM/YYYY"
          value-format="YYYY-MM-DD"
          :shortcuts="rangeShortcuts"
          :clearable="false"
          :editable="false"
          placement="bottom-start"
          popper-class="timesheet-geotraces-date-popper"
          @change="onFiltersChange"
        />
      </div>

      <el-radio-group v-model="kindFilter" size="default" @change="applyKindFilter">
        <el-radio-button :value="'All'">
          <i class="material-icons trace-filter-icon">all_inclusive</i>
          {{ $t('tools.geoTraceFilterAll') }}
        </el-radio-button>
        <el-radio-button :value="'ClockIn'">
          <i class="material-icons trace-filter-icon">login</i>
          {{ $t('timeRegistration.clockIn') }}
        </el-radio-button>
        <el-radio-button :value="'ClockOut'">
          <i class="material-icons trace-filter-icon">logout</i>
          {{ $t('timeRegistration.clockOut') }}
        </el-radio-button>
      </el-radio-group>

      <span class="text-muted text-sm ms-auto">
        {{ filteredTraces.length }} {{ $t('tools.geoTraces').toLowerCase() }}
      </span>
    </div>

    <div v-if="loading" class="text-muted">{{ $t('common.loading') || 'Loading...' }}</div>
    <div v-else-if="filteredTraces.length === 0" class="text-muted">{{ $t('users.noTimesheetGeoTraces') }}</div>
    <template v-else>
      <div class="row geotraces-layout">
        <div class="col-12 col-lg-8 order-1 order-lg-2 mb-3 mb-lg-0">
          <div id="timesheetGeoTracesMap" class="map map-big"></div>
        </div>
        <div class="col-12 col-lg-4 order-2 order-lg-1">
          <ul class="list-group trace-list">
            <li
              v-for="t in filteredTraces"
              :key="traceKey(t)"
              class="list-group-item d-flex align-items-center trace-row"
              :class="{ active: isActive(t) }"
              @mouseenter="highlightTrace(t)"
              @click="highlightTrace(t)"
            >
              <span class="trace-icon me-3" :class="iconClassFor(t)">
                <i class="material-icons">{{ iconFor(t) }}</i>
              </span>
              <div class="flex-grow-1 min-width-0">
                <div class="fw-semibold text-truncate">{{ titleFor(t) }}</div>
                <div class="text-sm text-muted text-truncate">{{ subtitleFor(t) }}</div>
                <div class="text-sm">
                  {{ formatTimestamp(t.timestamp) }}
                  &middot;
                  {{ t.latitude.toFixed(5) }}, {{ t.longitude.toFixed(5) }}
                </div>
              </div>
              <a
                role="button"
                class="ms-2"
                :title="$t('tools.showOnMap') || 'Show on map'"
                @click.stop="focusOnTrace(t)"
              >
                <i class="material-icons">place</i>
              </a>
            </li>
          </ul>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import api from '@/services/api';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import { ElDatePicker, ElRadioGroup, ElRadioButton } from 'element-plus';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const KIND = { ClockIn: 'ClockIn', ClockOut: 'ClockOut' };

export default {
  name: 'TimesheetGeoTraces',
  components: { ElDatePicker, ElRadioGroup, ElRadioButton },
  props: {
    userId: { type: String, required: true },
  },
  data() {
    const toIsoDate = d => {
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    };
    const today = new Date();
    const monthAgo = new Date();
    monthAgo.setDate(monthAgo.getDate() - 30);
    return {
      traces: [],
      kindFilter: 'All',
      loading: true,
      map: null,
      markers: {},
      activeTraceKey: null,
      isMobileViewport: window.innerWidth < 768,
      dateRange: [toIsoDate(monthAgo), toIsoDate(today)],
      rangeShortcuts: [
        { text: this.$t('tools.rangeLast7Days') || 'Last 7 days', value: () => { const e = new Date(); const s = new Date(); s.setDate(s.getDate() - 7); return [s, e]; } },
        { text: this.$t('tools.rangeLast30Days') || 'Last 30 days', value: () => { const e = new Date(); const s = new Date(); s.setDate(s.getDate() - 30); return [s, e]; } },
        { text: this.$t('tools.rangeLast90Days') || 'Last 90 days', value: () => { const e = new Date(); const s = new Date(); s.setDate(s.getDate() - 90); return [s, e]; } },
        { text: this.$t('tools.rangeLastYear') || 'Last year', value: () => { const e = new Date(); const s = new Date(); s.setFullYear(s.getFullYear() - 1); return [s, e]; } },
      ],
    };
  },
  computed: {
    filteredTraces() {
      if (this.kindFilter === 'All') return this.traces;
      return this.traces.filter(t => t.kind === this.kindFilter);
    },
  },
  async mounted() {
    await this.fetchTraces();
    this.loading = false;
    this.$nextTick(() => this.initMap());
    window.addEventListener('resize', this.handleMapResize);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleMapResize);
    if (this._mapResizeTimer) clearTimeout(this._mapResizeTimer);
    this.teardownMap();
  },
  methods: {
    handleMapResize() {
      this.isMobileViewport = window.innerWidth < 768;
      if (!this.map) return;
      if (this._mapResizeTimer) clearTimeout(this._mapResizeTimer);
      this._mapResizeTimer = setTimeout(() => { if (this.map) this.map.invalidateSize(); }, 100);
    },
    teardownMap() {
      if (this.map) { this.map.remove(); this.map = null; this.markers = {}; }
    },
    async fetchTraces() {
      try {
        const params = { dateFrom: this.dateRange[0], dateTo: this.dateRange[1], userId: this.userId };
        const r = await api.get('/Timesheet', { params });
        const days = r.data?.days || [];
        const flat = [];
        for (const d of days) {
          for (const e of d.entries || []) {
            if (e.clockInLatitude != null && e.clockInLongitude != null && e.realClockIn) {
              flat.push({
                id: e.id,
                kind: KIND.ClockIn,
                timestamp: e.realClockIn,
                latitude: e.clockInLatitude,
                longitude: e.clockInLongitude,
                projectName: e.projectName,
                projectKey: e.projectKey,
                timeRegistrationGroupName: e.timeRegistrationGroupName,
              });
            }
            if (e.clockOutLatitude != null && e.clockOutLongitude != null && e.realClockOut) {
              flat.push({
                id: e.id,
                kind: KIND.ClockOut,
                timestamp: e.realClockOut,
                latitude: e.clockOutLatitude,
                longitude: e.clockOutLongitude,
                projectName: e.projectName,
                projectKey: e.projectKey,
                timeRegistrationGroupName: e.timeRegistrationGroupName,
              });
            }
          }
        }
        flat.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
        this.traces = flat;
      } catch (error) {
        console.error('API request error:', error); // eslint-disable-line no-console
        this.$toast?.danger(this.$t('common.unableToRetrieveData'), { timeout: 0 });
      }
    },
    async onFiltersChange() {
      this.loading = true;
      this.activeTraceKey = null;
      this.teardownMap();
      await this.fetchTraces();
      this.loading = false;
      this.$nextTick(() => this.initMap());
    },
    applyKindFilter() {
      this.activeTraceKey = null;
      this.teardownMap();
      this.$nextTick(() => this.initMap());
    },
    iconFor(trace) { return trace.kind === KIND.ClockIn ? 'login' : 'logout'; },
    iconClassFor(trace) { return trace.kind === KIND.ClockIn ? 'trace-icon-clockin' : 'trace-icon-clockout'; },
    titleFor(trace) {
      const action = trace.kind === KIND.ClockIn ? this.$t('timeRegistration.clockIn') : this.$t('timeRegistration.clockOut');
      const project = trace.projectName || trace.timeRegistrationGroupName;
      return project ? `${action} — ${project}` : action;
    },
    subtitleFor(trace) {
      return trace.kind === KIND.ClockIn ? this.$t('timeRegistration.inTime') : this.$t('timeRegistration.outTime');
    },
    formatTimestamp(ts) {
      if (!ts) return '';
      try { return new Date(ts).toLocaleString(); } catch { return ts; }
    },
    initMap() {
      const el = document.getElementById('timesheetGeoTracesMap');
      if (!el) return;
      this.map = L.map('timesheetGeoTracesMap');
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom: 19 }).addTo(this.map);

      const points = this.filteredTraces.filter(t => t.latitude != null && t.longitude != null);
      if (points.length === 0) {
        this.map.setView([65.6835, -18.1009], 5);
        return;
      }
      points.forEach(t => {
        const marker = L.marker([t.latitude, t.longitude], { icon: this.markerIconFor(t) })
          .addTo(this.map)
          .bindPopup(this.popupHtml(t));
        this.markers[this.traceKey(t)] = marker;
      });
      if (points.length === 1) {
        this.map.setView([points[0].latitude, points[0].longitude], 15);
      } else {
        const bounds = L.latLngBounds(points.map(p => [p.latitude, p.longitude]));
        this.map.fitBounds(bounds.pad(0.2));
      }
    },
    markerIconFor(trace) {
      const colorClass = trace.kind === KIND.ClockIn ? 'trace-marker-clockin' : 'trace-marker-clockout';
      return L.divIcon({
        className: 'timesheet-geotrace-marker',
        html: `<div class="marker-bubble ${colorClass}"><i class="material-icons">${this.iconFor(trace)}</i></div>`,
        iconSize: [36, 36],
        iconAnchor: [18, 18],
      });
    },
    popupHtml(trace) {
      const title = this.escape(this.titleFor(trace));
      const subtitle = this.escape(this.subtitleFor(trace));
      const ts = this.escape(this.formatTimestamp(trace.timestamp));
      return `<div><strong>${title}</strong><br>${subtitle}<br><small>${ts}</small></div>`;
    },
    escape(s) {
      if (s == null) return '';
      return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    },
    traceKey(trace) { return `${trace.kind}-${trace.id}`; },
    isActive(trace) { return this.activeTraceKey === this.traceKey(trace); },
    highlightTrace(trace) {
      const key = this.traceKey(trace);
      const m = this.markers[key];
      if (!m || !this.map) return;
      this.activeTraceKey = key;
      m.openPopup();
    },
    focusOnTrace(trace) {
      const key = this.traceKey(trace);
      const m = this.markers[key];
      if (!m || !this.map) return;
      this.activeTraceKey = key;
      this.map.setView(m.getLatLng(), 16);
      m.openPopup();
    },
  },
};
</script>

<style scoped>
.map-big {
  height: 420px;
  width: 100%;
}

@media (min-width: 992px) {
  .map-big { height: 600px; }
  .trace-list { max-height: 600px; overflow-y: auto; }
}

.min-width-0 { min-width: 0; }

.trace-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  color: #fff;
  flex-shrink: 0;
}

.trace-icon i { font-size: 20px; }
.trace-icon-clockin { background-color: #2dce89; }
.trace-icon-clockout { background-color: #fb6340; }

.trace-row {
  cursor: pointer;
  transition: background-color 0.12s ease;
}
.trace-row:hover, .trace-row.active { background-color: rgba(94, 114, 228, 0.08); }

.range-picker-row .form-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #344767;
}

.range-picker-row :deep(.el-date-editor) {
  --el-date-editor-daterange-width: 280px;
  width: auto;
  min-width: 280px;
}

.range-picker-row :deep(.el-radio-button__inner) {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.trace-filter-icon { font-size: 16px; vertical-align: middle; }

@media (max-width: 575.98px) {
  .range-picker-row { flex-direction: column; align-items: stretch; }
  .range-picker-row > .d-flex { flex-direction: column; align-items: stretch !important; gap: 4px !important; }
  .range-picker-row :deep(.el-date-editor) { width: 100% !important; min-width: 0; }
  .range-picker-row :deep(.el-radio-group) { display: flex; width: 100%; }
  .range-picker-row :deep(.el-radio-group .el-radio-button) { flex: 1 1 0; min-width: 0; }
  .range-picker-row :deep(.el-radio-group .el-radio-button__inner) { width: 100%; justify-content: center; padding-left: 8px; padding-right: 8px; }
  .range-picker-row .ms-auto { margin-left: 0 !important; text-align: right; }
}
</style>

<style>
.timesheet-geotrace-marker .marker-bubble {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  border: 2px solid #fff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.4);
}
.timesheet-geotrace-marker .marker-bubble i { font-size: 20px; }
.timesheet-geotrace-marker .trace-marker-clockin { background-color: #2dce89; }
.timesheet-geotrace-marker .trace-marker-clockout { background-color: #fb6340; }

.timesheet-geotraces-date-popper.el-popper { z-index: 9999 !important; }

@media (max-width: 767.98px) {
  .timesheet-geotraces-date-popper.el-popper { max-width: calc(100vw - 16px) !important; width: auto !important; }
  .timesheet-geotraces-date-popper .el-picker-panel__sidebar { width: 100px !important; }
  .timesheet-geotraces-date-popper .el-picker-panel__body { margin-left: 100px !important; }
  .timesheet-geotraces-date-popper .el-picker-panel__shortcut { padding-left: 8px !important; font-size: 12px !important; }
}
</style>
